import React from 'react'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import 'rc-steps/assets/index.css'
import ReactCountryFlag from 'react-country-flag'
import reactStringReplace from 'react-string-replace'

import { palette, fonts, mediaQueries } from '../../../styles/variables'
import { translate } from '../../../utils/translation'
import {
  kissmetricsRecordingEvent,
  KISSMETRICS_TRACK_EVENTS,
} from '../../../utils/kissmetrics'
import {
  getUserTier,
  isPaidUser,
  isProUser,
} from '../../../utils/freeVersionBlock'
import { Text } from '../../../components/topography'
import {
  CloseButton,
  LinkButton,
  OutlineButton,
} from '../../../components/buttons/Buttons'
import { generateKey, generateRegExp } from '../../../utils/functions'
import { getUserFreeTrialDays, isSubUser } from '../../../utils/userAuth'
import LanguageLocationSelector from '../../../components/selector/LanguageLocationSelector'
import { getLocationInfo } from '../../../utils/locations'
import { selectCachedLocations } from '../../../state/selectors/locationManager'

import {
  StepWrapper,
  NavButtons,
  NavButton,
  StepContent,
  StepContentWrapper,
  SelectionsWrapper,
  SelectedLocation,
} from './styled'

class Step2 extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      currentLocId: null,
      currentLang: null,
    }
  }

  handleChangeLanguage = (locId, lang) => {
    this.setState({
      currentLocId: locId,
      currentLang: lang,
    })
  }

  render() {
    const {
      nextStep,
      previousStep,
      onSubmit,
      cachedLocations,
      locations,
      removeLocation,
      addLocation,
      showUpgradeModal,
      validate,
    } = this.props
    const { currentLocId, currentLang } = this.state
    const ctaText = isPaidUser()
      ? translate('upgrade_your_plan')
      : translate('start_free_trial_button', getUserFreeTrialDays())
    const locationCount = isProUser() ? 20 : 2
    const upgradeText = isProUser()
      ? translate('tier1_upgrade_to_add_locations', ctaText, locationCount)
      : translate('free_user_upgrade_to_add_locations', ctaText, locationCount)
    const listRegExp = generateRegExp([ctaText, `<b>${locationCount}</b>`])
    const userTier = getUserTier()
    const disableAddLocButton =
      !currentLang ||
      !currentLocId ||
      locations.some(
        (loc) =>
          generateKey([loc.languageCode, loc.id]) ===
          generateKey([currentLang, currentLocId]),
      )
    const isSubUserAccount = isSubUser()
    return (
      <StepWrapper width="fit-content">
        <StepContent>
          <StepContentWrapper>
            <LanguageLocationSelector
              autoSelect={false}
              listHeight={150}
              allowCities
              theme={{
                color: palette.darkGray,
                bgColor: palette.lightGray10,
                borderColor: palette.lightGray,
                height: 40,
              }}
              lang={currentLang}
              locId={currentLocId}
              style={{
                display: 'flex',
                width: '100%',
              }}
              onChange={this.handleChangeLanguage}
              getSupportedLocationTooltip={(languages, optionValue) => {
                if (!languages?.length) return `<b>${optionValue}</b>`
                return `<b>${optionValue}:</b></br>${translate(
                  'location_other_languages_message',
                  languages
                    .map((l) => `"<b>${translate(l.code)}</b>"`)
                    .join(', '),
                )}`
              }}
              getUnsupportedLocationTooltip={(languages, optionValue) => {
                if (!languages?.length) return `<b>${optionValue}</b>`
                return `<b>${optionValue}:</b></br>${translate(
                  'location_disabled_message',
                  languages
                    .map((l) => `"<b>${translate(l.code)}</b>"`)
                    .join(', '),
                )}`
              }}
            />
            <OutlineButton
              static
              data-testid="add-project-location-add-button"
              color={palette.orange}
              onClick={() => {
                addLocation({
                  ...getLocationInfo(currentLocId, cachedLocations),
                  languageCode: currentLang,
                })
                this.setState({
                  currentLang: null,
                  currentLocId: null,
                })
              }}
              style={{
                marginLeft: '8px',
                height: '40px',
                whiteSpace: 'pre',
              }}
              styles={{
                [mediaQueries.tablet]: {
                  width: '100%',
                  marginTop: '20px',
                  marginLeft: 0,
                },
              }}
              disabled={disableAddLocButton}
            >
              {translate('add_payment')}
            </OutlineButton>
          </StepContentWrapper>
          {locations.length === 0 && (
            <Text
              color={palette.danger}
              margin="16px 0"
              fontSize="12px"
              textAlign="center"
            >
              {translate('no_location_error_message')}
            </Text>
          )}
          <SelectionsWrapper>
            {locations.map((selection) => (
              <SelectedLocation
                data-testid="selected-language-locations"
                key={generateKey([selection.languageCode, selection.id])}
              >
                <ReactCountryFlag
                  countryCode={selection.countryIsoCode}
                  svg
                  style={{
                    marginTop: '-3px',
                    width: '1.3333em',
                    height: '1em',
                  }}
                />
                <span
                  style={{
                    padding: '0 20px',
                    fontSize: '14px',
                  }}
                >
                  {selection.type === 'Country'
                    ? `${translate(selection.languageCode)} / ${translate(
                        selection.name,
                      )}`
                    : `${translate(selection.languageCode)} / ${translate(
                        selection.canonicalName,
                      )}`}
                </span>
                <CloseButton
                  data-testid="selected-location-close"
                  color={palette.gray}
                  style={{
                    top: '11px',
                    right: '8px',
                  }}
                  onClick={() => removeLocation(selection)}
                />
              </SelectedLocation>
            ))}
          </SelectionsWrapper>
          {userTier !== 'tier2' && userTier !== 'tier3' && (
            <Text
              lineHeight="22px"
              boldColor={palette.darkGray}
              textAlign="center"
              margin="16px 0 0 0"
            >
              {reactStringReplace(upgradeText, listRegExp, (match, index) => {
                if (match.startsWith('<b>')) {
                  return <b key={index}>{locationCount}</b>
                }
                return (
                  <LinkButton
                    key={index}
                    onClick={showUpgradeModal}
                    disabled={isSubUserAccount}
                    style={{
                      textDecoration: 'underline',
                      fontSize: '14px',
                      margin: '0px',
                      display: 'inline-block',
                      fontFamily: fonts.primaryBook,
                    }}
                  >
                    {ctaText}
                  </LinkButton>
                )
              })}
            </Text>
          )}
        </StepContent>
        <NavButtons>
          <NavButton
            testId="add-project-step2-close"
            color="grey"
            onClick={previousStep}
            tag={translate('back')}
          />
          <NavButton
            testId="add-project-step2-next"
            color="orange"
            onClick={(event) => {
              const { invalidLocations } = validate()
              if (!invalidLocations) {
                kissmetricsRecordingEvent(
                  KISSMETRICS_TRACK_EVENTS.project_creation_actions,
                  {
                    'project flow action': 'add location next',
                  },
                )
                onSubmit(
                  locations.map((selection) => ({
                    loc_id: selection.id,
                    lang: selection.languageCode,
                    ...selection,
                  })),
                )
                nextStep(event)
              }
            }}
            tag={translate('next')}
            disabled={locations.length === 0}
          />
        </NavButtons>
      </StepWrapper>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  cachedLocations: selectCachedLocations(),
})

export default connect(mapStateToProps)(Step2)
